import { Link } from "react-router-dom";
import Page from "@/components/layout/Page";
import strokeBrain from "@/assets/mental-health/stroke-brain.png";
import nhsBadge from "@/assets/nhs-mental-health-badge.png";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";

export default function Strokes() {
  return (
    <>
      <SEOHead
        title="Stroke Recovery | Tactile Rehabilitation Tools | Samboards"
        description="Handmade tactile sensory tools for stroke rehabilitation and recovery. Improve motor skills, dexterity & neurological rehabilitation. NHS recommended. UK made."
        canonical="https://samboards.co.uk/mental-health/strokes"
        keywords="stroke recovery tools, stroke rehabilitation tools, tactile stroke therapy, motor skills recovery, neurological rehabilitation, NHS stroke support, Headway tools, UK handmade"
        ogImage={strokeBrain}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Mental Health", url: "/mental-health/strokes" },
          { name: "Strokes", url: "/mental-health/strokes" }
        ]}
      />
      
      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <Page>
        <section className="w-full pb-16 sm:pb-20 md:pb-24">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4 sm:gap-6 mb-4 sm:mb-6">
            <h1 className="text-[36px] sm:text-[46px] lg:text-[54px] leading-[1.1] font-bold page-title-gradient flex-1">Strokes</h1>
            <a 
              href="https://www.nhs.uk/mental-health/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex-shrink-0 transition-all duration-200 hover:scale-105 hover:opacity-90 touch-manipulation"
              aria-label="NHS Mental Health Support"
            >
              <img 
                src={nhsBadge} 
                alt="NHS - Here to help with mental health" 
                className="h-12 sm:h-16 md:h-20 w-auto object-contain"
              />
            </a>
          </div>
          
          <div className="mental-health-hero-image">
            <img 
              src={strokeBrain} 
              alt="Stroke impact on brain illustrated with medical imaging"
            />
          </div>
          
          <div className="space-y-6 max-w-3xl">
            <p className="text-xl text-muted-foreground leading-relaxed">
              A stroke occurs when the brain is deprived of sufficient oxygen and nutrients.
            </p>
            
            <p className="text-lg leading-relaxed">
              A stroke is a cerebrovascular disease which causes a blood clot or a bleed in the brain which may lead to permanent neurological damage and complications. Because the brain needs a never ending supply of oxygen and nutrients to work efficiently, even starvation of the blood supply for a short period of time can cause problems as the brain cells begin to die. Often resulting in paralysis and sometimes death.
            </p>
            
            <p className="text-lg leading-relaxed">
              The part of the brain which regulates sensation is known as the 'thalamus', which is responsible for relaying 98% of sensory input. So, it stands to reason that the vast majority of thalamic stroke patients will often display numbness and sensory issues.
            </p>
            
            <p className="text-lg leading-relaxed">
              Successful recovery from a stroke will often involve specific therapies and support systems, including multi-discipline therapies which will help with speech impairment, movement and coordination, tiredness, daily activities including reading and writing, mood swings, lack of concentration and memory problems.
            </p>
            
            <p className="text-lg leading-relaxed">
              Therapy, such as 'sensory retraining' often includes the use of tactile sensory products, such as the Samboards Sensory Touch Pad, to help regain sensation after a stroke.
            </p>
          </div>

          {/* Related Resources Section */}
          <div className="mt-16 pt-12 border-t border-slate-700/40">
            <h2 className="text-3xl font-semibold mb-3 bg-gradient-to-r from-emerald-200 to-teal-200 bg-clip-text text-transparent">
              Recommended Products for Stroke Recovery
            </h2>
            <p className="text-slate-300/80 mb-8 max-w-2xl">
              These sensory tools support sensory retraining and rehabilitation following a stroke.
            </p>

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <Link
                to="/samboards"
                className="group rounded-2xl bg-slate-900/75 border border-slate-700/80 hover:border-emerald-300/60 shadow-[0_16px_45px_rgba(15,23,42,0.8)] p-5 flex flex-col interactive-card"
              >
                <h3 className="font-heading text-xl mb-2 text-emerald-100">Samboards</h3>
                <p className="text-sm text-slate-200/90 mb-4">Full-size boards with varied textures for comprehensive sensory retraining.</p>
                <span className="mt-auto inline-flex items-center gap-2 text-sm font-medium text-emerald-300">
                  View product
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </span>
              </Link>

              <Link
                to="/sensory-pads"
                className="group rounded-2xl bg-slate-900/75 border border-slate-700/80 hover:border-emerald-300/60 shadow-[0_16px_45px_rgba(15,23,42,0.8)] p-5 flex flex-col interactive-card"
              >
                <h3 className="font-heading text-xl mb-2 text-emerald-100">Sensory Pads</h3>
                <p className="text-sm text-slate-200/90 mb-4">Tactile therapy tools to help regain sensation and support recovery.</p>
                <span className="mt-auto inline-flex items-center gap-2 text-sm font-medium text-emerald-300">
                  View product
                  <span className="transition-transform group-hover:translate-x-0.5">→</span>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
      </Page>
    </div>
    </>
  );
}
